<template>
  <div
    id="conteudo-acao"
    v-if="!carregando && dadosAcaoResponse"
    v-show="!telaAberta"
  >
    <Breadcrumb :items="breadcrumbItems" />

    <div id="titulo">
      <h2>{{ dadosAcao?.titulo }}</h2>
      <h4>Coordenador: {{ formatarEnum(dadosAcao?.nomeCoordenador) }}</h4>
    </div>

    <!-- MENU DE NAVEGAÇÃO RESPONSIVO -->
    <section id="menu-container">
      <div id="menus">
        <div
          class="item"
          :class="{ 'opcao-selecionada': $route.path.includes('informacoes') }"
          v-if="acaoId"
        >
          <router-link :to="`/acao/${acaoId}/informacoes`">
            Sobre a ação
          </router-link>
        </div>

        <div
          class="item"
          :class="{
            'opcao-selecionada':
              $route.path.includes('atividades') &&
              !$route.path.includes('cadastrar-atividade'),
          }"
        >
          <router-link :to="`/acao/${acaoId}/atividades`">
            Atividades
          </router-link>
        </div>

        <div
          class="item"
          :class="{
            'opcao-selecionada':
              $route.path.includes('noticias') &&
              !$route.path.includes('cadastrar-noticia'),
          }"
        >
          <router-link :to="`/acao/${acaoId}/noticias`"> Notícias </router-link>
        </div>
        <div
          class="item"
          :class="{
            'opcao-selecionada': $route.path.includes('vagas'),
          }"
        >
          <router-link :to="`/acao/${acaoId}/vagas`"> Vagas </router-link>
        </div>
        <div
          class="item"
          :class="{
            'opcao-selecionada': $route.path.includes('parcerias'),
          }"
        >
          <router-link :to="`/acao/${acaoId}/parcerias`">
            Parcerias
          </router-link>
        </div>

        <div
          class="item"
          :class="{
            'opcao-selecionada': $route.path.includes('dashboard'),
          }"
        >
          <router-link :to="`/acao/${acaoId}/dashboard`">
            Dashboard
          </router-link>
        </div>

        <div
          class="item"
          :class="{
            'opcao-selecionada': $route.path.includes('central-coordenacao'),
            'ultimo-item': isCoordenadorDaAcao,
          }"
          v-if="isCoordenadorDaAcao"
        >
          <router-link :to="`/acao/${acaoId}/central-coordenacao`">
            Central da Coordenação
          </router-link>
        </div>
      </div>
    </section>

    <!-- BOTÕES DE CADASTRO (condicionais) - MANTENDO A MESMA ESTRUTURA -->

    <!-- PARTE DINÂMICA - ROTEAMENTO -->
    <div id="conteudo-dinamico">
      <router-view
        :class="{
          cadastro:
            !$route.path.includes('central-coordenacao') &&
            !$route.path.includes('informacoes') &&
            !$route.path.includes('parcerias') &&
            !$route.path.includes('dashboard'),
        }"
        :acaoId="acaoId"
        :isCoordenador="isCoordenadorDaAcao"
        @atividadeSelecionada="abrirTela('atividade', $event)"
        @noticiaSelecionada="abrirTela('noticia', $event)"
        @abrir-tarefa="abrirTela('tarefa', $event)"
      />
    </div>
  </div>

  <carregamento v-if="carregando" />
</template>

<script setup>
import { ref, computed, watch, onMounted } from "vue";
import { useRoute } from "vue-router";
import acaoService from "@/service/acaoService";
import userInfos from "@/utils/userInfos";
import { formatarEnum } from "@/utils/FormatarEntradas";
import Carregamento from "@/components/common/Carregamento.vue";
import Breadcrumb from "@/components/common/Breadcrumb.vue";

const route = useRoute();

const carregando = ref(false);
const dadosAcaoResponse = ref({});
const acaoId = ref(route.params.id);
const isCoordenador = ref(false);
const isCoordenadorDaAcao = ref(false);

const telaAberta = ref(false);
const atividadeSelecionada = ref(false);
const noticiaSelecionada = ref(false);
const tarefaSelecionada = ref(false);
const atividadeAberta = ref(null);
const noticiaAberta = ref(null);
const tarefaAberta = ref(null);

const breadcrumbItems = computed(() => {
  return [
    { label: "Início", to: "/" },
    { label: "Ações", to: "/suas-acoes" },
    { tipo: "acao", id: acaoId.value, to: `/acao/${acaoId.value}` },
    {
      label: nomeDaRotaAtual.value,
      to: `/acao/${acaoId.value}/${rotaAtual.value}`,
    },
  ];
});

const dadosAcao = computed(() => {
  return (
    dadosAcaoResponse.value || {
      titulo: "Título Padrão",
      nomeCoordenador: "Coordenador Padrão",
      tipoAcao: "Projeto",
    }
  );
});

const mapeamentoRotas = {
  atividades: "Atividades",
  noticias: "Notícias",
  informacoes: "Informações",
  declaracoes: "Minhas Declarações",
  tarefas: "Tarefas",
  vagas: "Vagas",
  parcerias: "Parcerias",
  dashboard: "Dashboard",
  "central-coordenacao": "Central da Coordenação",
};

const rotaAtual = computed(() => {
  const path = route.path;
  for (const rota of Object.keys(mapeamentoRotas)) {
    if (path.includes(rota)) {
      return rota;
    }
  }
  return "";
});

const nomeDaRotaAtual = computed(() => {
  return mapeamentoRotas[rotaAtual.value] || "Página Atual";
});

const podeNavegarParaRotaAtual = computed(() => {
  const rotasSemLink = [
    "cadastrar-atividade",
    "cadastrar-noticia",
    "cadastrar-tarefa",
  ];
  return !rotasSemLink.some((rota) => route.path.includes(rota));
});

async function buscarDadosAcao() {
  carregando.value = true;
  try {
    const response = await acaoService.getAcoesById(acaoId.value);
    dadosAcaoResponse.value = response;
    dadosAcaoResponse.value.titulo = dadosAcaoResponse.value.titulo.replace(
      /"/g,
      ""
    );
    acaoId.value = response.id;

    // Verificar se é coordenador desta ação específica
    try {
      const ehCoord = await acaoService.verificarCoordenador(acaoId.value);
      isCoordenadorDaAcao.value = !!ehCoord;
    } catch (e) {
      isCoordenadorDaAcao.value = false;
    }
  } catch (error) {
    console.error("Erro ao buscar dados da ação:", error);
  } finally {
    carregando.value = false;
  }
}

function checkCoordenador() {
  const userScope = userInfos.getUserInfos().scope;
  isCoordenador.value = userScope.split(" ").includes("SCOPE_COORDENADOR");
}

function abrirTela(tela, dados) {
  telaAberta.value = true;

  if (tela === "atividade") {
    atividadeSelecionada.value = true;
    atividadeAberta.value = dados;
  } else if (tela === "noticia") {
    noticiaSelecionada.value = true;
    noticiaAberta.value = dados;
  } else if (tela === "tarefa") {
    tarefaSelecionada.value = true;
    tarefaAberta.value = dados;
  }
}

function fecharTela(tela) {
  telaAberta.value = false;

  if (tela === "atividade") {
    atividadeSelecionada.value = false;
    atividadeAberta.value = null;
  } else if (tela === "noticia") {
    noticiaSelecionada.value = false;
    noticiaAberta.value = null;
  } else if (tela === "tarefa") {
    tarefaSelecionada.value = false;
    tarefaAberta.value = null;
  }
}

function analiseTipoAcao() {
  return dadosAcao.value.tipoAcao === "Curso" ||
    dadosAcao.value.tipoAcao === "Evento"
    ? "Meus Certificados"
    : "Minhas Declarações";
}

watch(
  () => route.params.id,
  async (novoId) => {
    if (novoId) {
      acaoId.value = novoId;
      await buscarDadosAcao();
    }
  },
  { immediate: true }
);

onMounted(() => {
  checkCoordenador();
});
</script>

<style scoped>
#conteudo-acao {
  display: block;
  background-color: transparent;
  padding: 20px;
  border-radius: 10px;
  border-bottom: 1px solid rgb(211, 211, 211);
}

#titulo {
  padding-bottom: 20px;
  border-radius: 10px 10px 0 0;
}

#titulo h2 {
  font-size: 1.8rem;
  color: #343a40;
  margin-bottom: 0.5rem;
}

#titulo h4 {
  font-size: 1rem;
  color: #6c757d;
  margin: 0;
}

#menus {
  display: flex;
  justify-content: center;
  flex-direction: row;
  flex-wrap: wrap;
  width: 100%;
  border-radius: 5px;
  overflow: hidden;
  border: 1px solid #48298a !important;
}

.item {
  display: flex;
  flex: 1;
  min-width: 160px;
}

#menus .item a {
  width: 100%;
  border: none;
  padding: 12px 15px;
  font-size: 1rem;
  cursor: pointer;
  transition:
    background-color 0.3s ease,
    color 0.3s ease;
  color: white;
  background-color: #362065;
  text-decoration: none;
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  word-break: break-word;
  white-space: normal;
}

.ultimo-item a {
  border-right: none !important;
}

.opcao-selecionada a {
  background-color: white !important;
  color: black !important;
}

#menus .item a:hover {
  background-color: rgba(54, 32, 101, 0.7);
  color: #ffffff;
}

@media (max-width: 1024px) {
  .item {
    flex: 1 1 33.333%;
    min-width: 140px;
  }

  #menus .item a {
    padding: 10px 12px;
    font-size: 0.95rem;
  }
}
@media (max-width: 768px) {
  .item {
    flex: 1 1 50%;
    min-width: 120px;
  }

  #menus .item a {
    padding: 10px 8px;
    font-size: 0.9rem;
  }

  #titulo h2 {
    font-size: 1.6rem;
  }

  #titulo h4 {
    font-size: 0.9rem;
  }
}

@media (max-width: 576px) {
  #menus {
    flex-direction: column;
    border-top: none;
  }

  .item {
    flex: 1;
    min-width: 100%;
    border-bottom: 1px solid #ddd;
  }

  #menus .item a {
    padding: 12px 15px;
    font-size: 1rem;
    border-right: none;
    border-bottom: 1px solid white;
    justify-content: center;
    text-align: center;
  }

  .ultimo-item a {
    border-bottom: none !important;
  }

  .opcao-selecionada a {
    background-color: #f0f0f0 !important;
    font-weight: bold;
  }

  #titulo {
    padding: 15px 0 15px 15px;
  }

  #titulo h2 {
    font-size: 1.4rem;
  }

  #titulo h4 {
    font-size: 0.85rem;
  }
}

@media (max-width: 375px) {
  #menus .item a {
    padding: 10px 12px;
    font-size: 0.9rem;
  }

  #titulo h2 {
    font-size: 1.3rem;
  }
}

#conteudo-dinamico {
  min-height: 50vh;
}
@media (max-width: 576px) {
  #conteudo-dinamico {
    padding: 0 15px;
  }
}
.spinner {
  text-align: center;
  padding: 20px;
  font-size: 1.2rem;
  color: #362065;
}
</style>
